// ============================================
// Package Management Types
// ============================================

export interface PackageMetadata {
  color: string; // e.g., "#3B82F6"
  icon: string; // e.g., "Zap", "Crown", "Shield"
}

/**
 * Package - Represents a subscription package
 */
export interface Package {
  publicId: string;
  code: string;
  name: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  highlightsEn?: string | null;
  highlightsVi?: string | null;
  price: number;
  priceVnd?: number;
  prices?: PackagePriceItem[];
  duration: number;
  maxAgents: number;
  tokensPerMonth: number;
  advanceTokensPerMonth: number;
  isActive: boolean;
  allowDynamicTheme: boolean;
  features?: string[];
  /** Usage limit per feature code (e.g. projects); null/absent = unlimited. */
  featureLimits?: Record<string, number | null>;
  /** Usage limit per UTC day per feature code (media features); null/absent = unlimited. */
  featureDailyLimits?: Record<string, number | null>;
  integrationEntitlements?: string[];
  integrationLimits?: Record<string, number>;
  metadata?: string; // Optional JSON string: {"color": "#3B82F6", "icon": "Zap"}
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PackagePriceItem {
  publicId?: string;
  currency: 'USD' | 'VND' | string;
  amountMinor: string | number;
  billingPeriod?: string;
  isActive?: boolean;
}

export interface PackageFeatureCatalogItem {
  code: string;
  name: string;
  description: string;
  /** True when the feature accepts a usage limit (e.g. max projects). */
  supportsLimit?: boolean;
  /** True when the feature also accepts a per-UTC-day limit (e.g. files, images). */
  supportsDailyLimit?: boolean;
}

export interface PackageWithParsedMetadata extends Omit<Package, 'metadata'> {
  metadata: PackageMetadata;
}

// Helper function to safely parse metadata
export const parsePackageMetadata = (metadataString: string): PackageMetadata => {
  try {
    const parsed = JSON.parse(metadataString);
    return {
      color: parsed.color || '#3B82F6',
      icon: parsed.icon || 'Package',
    };
  } catch {
    return {
      color: '#3B82F6',
      icon: 'Package',
    };
  }
};

export interface UpdatePackageStatusInput {
  publicId: string;
  isActive: boolean;
}

export type PackageCapabilityQualityTier = 'low' | 'standard' | 'high';
export type PackageCapabilityCostTier = 'cheap' | 'normal' | 'expensive';

export interface PackageCapabilityAssignment {
  id: number;
  capabilityCode: string;
  capabilityName: string | null;
  capabilityDescription: string | null;
  capabilityPublicId: string | null;
  capabilityIsActive: boolean | null;
  isEnabled?: boolean;
  isCatalogCapability: boolean;
}

export interface PackageCapabilityAssignmentInput {
  capabilityCode: string;
  isEnabled?: boolean;
}

export interface PackageToolAssignment {
  toolId: string;
  name: string;
  description: string;
  source: string;
  riskLevel: string;
  allowedActions: string[];
  isEnabled: boolean;
  globalEnabled: boolean;
}

export interface PackageToolAssignmentInput {
  toolId: string;
  isEnabled: boolean;
}
